import { customBaseUrl, httpGet, httpPost } from '../../store/http';

const referralUrl = customBaseUrl.referralUrl;

/**
 * Helper to build clean query strings from params object
 */
const buildQuery = (params) => {
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

/**
 * POST /api/v1/marketer/assign
 * Manually attribute a customer to a marketer (admin)
 * Payload: { customerUserId: string, force: boolean, supportCode: string }
 */
export const assignCustomerToMarketer = async (payload) => {
  return await httpPost(`${referralUrl}/api/v1/marketer/assign`, payload);
};

/**
 * GET /api/v1/marketer/customer-lookup
 * Support desk: find a customer by email, phone, userId, name, ref code or account number
 */
export const lookupCustomerMarketer = async (query) => {
  const queryString = buildQuery({ query });
  return await httpGet(`${referralUrl}/api/v1/marketer/customer-lookup${queryString}`);
};

/**
 * GET /api/v1/marketer/customer/{customerUserId}/categories
 * What one customer transacts in, by category
 * Query params: from (YYYY-MM-DD), to (YYYY-MM-DD)
 */
export const getCustomerTransactionCategories = async (customerUserId, params = {}) => {
  const queryString = buildQuery(params);
  return await httpGet(`${referralUrl}/api/v1/marketer/customer/${encodeURIComponent(customerUserId)}/categories${queryString}`);
};

/**
 * GET /api/v1/marketer/list
 * List marketers with onboarding and transaction stats
 * Query params: pageNo, pageSize, query, sortBy (createdAt|name|supportCode|email), sortDir (ASC|DESC)
 */
export const getMarketersList = async (params = {}) => {
  const queryString = buildQuery(params);
  return await httpGet(`${referralUrl}/api/v1/marketer/list${queryString}`);
};

/**
 * POST /api/v1/marketer/repair-unresolved
 * Retry attribution for unresolved support codes (admin)
 */
export const repairUnresolvedAttributions = async (limit = 100) => {
  const queryString = buildQuery({ limit });
  return await httpPost(`${referralUrl}/api/v1/marketer/repair-unresolved${queryString}`, {});
};

/**
 * GET /api/v1/marketer/support-code/{supportCode}
 * One marketer with their stats
 */
export const getMarketerBySupportCode = async (supportCode) => {
  return await httpGet(`${referralUrl}/api/v1/marketer/support-code/${encodeURIComponent(supportCode)}`);
};

/**
 * GET /api/v1/marketer/support-code/{supportCode}/customers
 * Customers of a marketer with search, type (MERCHANT|CUSTOMER), date range, and sorting
 */
export const getMarketerCustomers = async (supportCode, params = {}) => {
  const queryString = buildQuery(params);
  return await httpGet(`${referralUrl}/api/v1/marketer/support-code/${encodeURIComponent(supportCode)}/customers${queryString}`);
};

/**
 * GET /api/v1/marketer/support-code/{supportCode}/summary
 * Everything about one marketer in a single call: headline stats, totals, category breakdown, top customers
 * Query params: from (YYYY-MM-DD), to (YYYY-MM-DD)
 */
export const getMarketerSummary = async (supportCode, params = {}) => {
  const queryString = buildQuery(params);
  return await httpGet(`${referralUrl}/api/v1/marketer/support-code/${encodeURIComponent(supportCode)}/summary${queryString}`);
};

/**
 * GET /api/v1/marketer/support-code/{supportCode}/transaction-stats
 * Transaction counts and volume per customer for a marketer
 * Query params: from (YYYY-MM-DD), to (YYYY-MM-DD)
 */
export const getMarketerTransactionStats = async (supportCode, params = {}) => {
  const queryString = buildQuery(params);
  return await httpGet(`${referralUrl}/api/v1/marketer/support-code/${encodeURIComponent(supportCode)}/transaction-stats${queryString}`);
};

/**
 * GET /api/v1/marketer/unattributed
 * Repair queue: customers carrying a support code that never resolved to a marketer
 */
export const getUnattributedCustomers = async (params = {}) => {
  const queryString = buildQuery(params);
  return await httpGet(`${referralUrl}/api/v1/marketer/unattributed${queryString}`);
};
